<script setup>
import { ref, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const store = useStore();
const { t } = useI18n();

const agentBots = useMapGetter('agentBots/getBots');
const uiFlags = useMapGetter('agentBots/getUIFlags');

// Get current user's external UUID for authentication
const currentUser = useMapGetter('getCurrentUser');
const getUserId = () => currentUser.value?.external_user_id;

const loading = ref({});

const toggleBot = async bot => {
  loading.value[bot.uuid] = true;
  try {
    await store.dispatch('agentBots/toggleBot', {
      botUuid: bot.uuid,
      enable: !bot.enabled,
    });
    useAlert(
      bot.enabled ? 'Bot disabled successfully' : 'Bot enabled successfully'
    );
  } catch (error) {
    useAlert('Failed to toggle bot status');
  } finally {
    loading.value[bot.uuid] = false;
  }
};

const openCreateBotPage = () => {
  if (typeof window === 'undefined') return;

  const userId = getUserId();
  const langbotApiUrl = window.chatwootConfig?.langbotApiUrl;

  // If no Langbot API URL is configured, show an alert
  if (!langbotApiUrl) {
    useAlert(
      'Langbot API URL not configured. Please contact your administrator.'
    );
    return;
  }

  const baseUrl = langbotApiUrl.replace('/api/v1', '');
  const url = userId
    ? `${baseUrl}/login?user_id=${userId}`
    : `${baseUrl}/login`;
  window.open(url, '_blank');
};

const refreshBots = () => {
  store.dispatch('agentBots/getVowchatBots');
};

onMounted(() => {
  store.dispatch('agentBots/getVowchatBots');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('AGENT_BOTS.LIST.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        title="Vowchat Agent Bots"
        description="Manage your Vowchat agent bots - enable or disable them as needed"
        link-text="Learn More"
        :href="
          (() => {
            if (typeof window === 'undefined') return '#';
            const langbotApiUrl = window.chatwootConfig?.langbotApiUrl;
            if (!langbotApiUrl) return '#';
            const userId = getUserId();
            const baseUrl = langbotApiUrl.replace('/api/v1', '');
            return userId
              ? `${baseUrl}/login?user_id=${userId}`
              : `${baseUrl}/login`;
          })()
        "
      >
        <template #actions>
          <Button
            icon="i-lucide-refresh-cw"
            label="Refresh"
            slate
            @click="refreshBots"
          />
          <Button
            icon="i-lucide-circle-plus"
            label="Create New Agent Bot"
            @click="openCreateBotPage"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <!-- Bot Cards Grid -->
    <div
      v-if="agentBots.length"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4"
    >
      <div
        v-for="bot in agentBots"
        :key="bot.uuid || bot.id"
        class="bg-white dark:bg-n-slate-1 rounded-lg shadow-sm border border-n-weak p-6 hover:shadow-md transition-shadow"
      >
        <!-- Bot Header -->
        <div class="flex items-start justify-between mb-4">
          <div class="flex items-center">
            <div
              class="w-12 h-12 rounded-full bg-n-blue-5 flex items-center justify-center mr-3"
            >
              <Icon icon="i-lucide-bot" class="text-n-blue-10 text-xl" />
            </div>
            <div>
              <h3 class="font-semibold text-lg text-n-slate-12">
                {{ bot.name || 'Unnamed Bot' }}
              </h3>
              <span
                class="text-sm"
                :class="bot.enabled ? 'text-green-600' : 'text-n-slate-10'"
              >
                {{ bot.enabled ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Bot Info -->
        <div class="mb-4 space-y-2">
          <div
            v-if="bot.adapter_config?.channel_name"
            class="text-sm text-n-slate-11"
          >
            <span class="font-medium">Channel:</span>
            {{ bot.adapter_config.channel_name }}
          </div>
        </div>

        <!-- Bot Actions -->
        <div class="flex justify-end">
          <Button
            :variant="bot.enabled ? 'ruby' : 'emerald'"
            :icon="bot.enabled ? 'i-lucide-x-circle' : 'i-lucide-check-circle'"
            :label="bot.enabled ? 'Disable' : 'Enable'"
            :is-loading="loading[bot.uuid]"
            @click="toggleBot(bot)"
          />
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!uiFlags.isFetching"
      class="flex flex-col items-center justify-center p-12 text-center"
    >
      <Icon icon="i-lucide-bot" class="text-4xl text-n-slate-8 mb-4" />
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        No Agent Bots Found
      </h3>
      <p class="text-n-slate-10 mb-4">
        You don't have any Vowchat agent bots yet. Create one to get started.
      </p>
      <Button
        icon="i-lucide-circle-plus"
        label="Create New Agent Bot"
        @click="openCreateBotPage"
      />
    </div>
  </SettingsLayout>
</template>
